import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { FormMode } from '../../../shared/types/forms.types';
import {
  OPTION_CATEGORY_LABEL,
  OPTION_CATEGORY_ORDER,
  OptionCategory,
  VehicleOption,
  VehicleOptionWriteRequest,
} from '../../../shared/types/option.types';

const CATEGORY_OPTIONS: PrettySelectOption<OptionCategory>[] =
  OPTION_CATEGORY_ORDER.map((c) => ({ value: c, label: OPTION_CATEGORY_LABEL[c] }));

/** Génère un slug URL-safe : minuscule, accents retirés, espaces → `-`,
 *  tout le reste hors `[a-z0-9-]` supprimé. Utilisé comme suggestion par
 *  défaut quand le user n'a pas encore touché le champ slug. */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

@Component({
  selector: 'app-option-form',
  imports: [CommonModule, ReactiveFormsModule, PrettySelectComponent],
  templateUrl: './option-form.component.html',
})
export class OptionFormComponent implements OnChanges {
  private readonly fb = inject(NonNullableFormBuilder);

  @Input() mode: FormMode = 'create';
  @Input() initial: VehicleOption | null = null;
  @Input() submitting = false;
  @Input() serverError: string | null = null;

  @Output() readonly save = new EventEmitter<VehicleOptionWriteRequest>();
  @Output() readonly cancel = new EventEmitter<void>();

  readonly categoryOptions = CATEGORY_OPTIONS;

  readonly form = this.fb.group({
    nameFr: ['', [Validators.required, Validators.maxLength(80)]],
    nameEn: [''],
    slug: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]],
    icon: [''],
    category: [null as OptionCategory | null],
  });

  /** Évite d'écraser le slug si le user l'a déjà personnalisé. */
  private slugTouched = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initial']) {
      this.applyInitial();
    }
  }

  private applyInitial(): void {
    const o = this.initial;
    this.form.reset({
      nameFr: o?.nameFr ?? '',
      nameEn: o?.nameEn ?? '',
      slug: o?.slug ?? '',
      icon: o?.icon ?? '',
      category: o?.category ?? null,
    });
    this.slugTouched = !!o?.slug;
  }

  /** Auto-fill slug à partir de nameFr en mode création tant que le user
   *  n'a pas modifié le slug manuellement. */
  onNameInput(value: string): void {
    if (this.mode !== 'create' || this.slugTouched) return;
    const slug = slugify(value);
    this.form.controls.slug.setValue(slug, { emitEvent: false });
  }

  markSlugTouched(): void {
    this.slugTouched = true;
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    this.save.emit({
      nameFr: raw.nameFr.trim(),
      nameEn: raw.nameEn.trim() || null,
      slug: raw.slug.trim(),
      icon: raw.icon.trim() || null,
      category: raw.category,
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  showError(name: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }
}
